import NumberFlow from '@number-flow/react';
import { useEffect, useRef, useState } from 'react';

import { FIXED_HEADER_CONTAINER_ID } from '@/components/Header/HeaderBase';
import { TextSkeleton } from '@/components/Skeleton';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';

import { btnStyles } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { InfoIconOutline } from '@/ui/Icons/InfoIconOutline';
import { SpinningRefreshIcon } from '@/ui/SpinningRefreshIcon';
import { Tooltip } from '@/ui/Tooltip';

import { classNames } from '@/utils/classnames';
import { isProd } from '@/utils/environment';

import { AllocationPieChart, AllocationPieChartSkeleton } from './AllocationPieChart';
import { PortfolioChainFilters } from './PortfolioChainFilters';
import { PortfolioChart, PortfolioChartSkeleton } from './PortfolioChart';
import { PortfolioWalletSelector } from './PortfolioWalletSelector';
import type { PortfolioHeaderProps } from './types';

/** Wallet-level PnL is parked until the numbers are trusted; the cells stay wired up. */
const SHOW_WALLET_PNL = false;

export function PortfolioHeader({
  totalValue,
  isLoading,
  pnl,
  isPnLLoading,
  portfolio,
  isPortfolioLoading,
  chart,
  isChartLoading,
  chartPeriod,
  onChartPeriodChange,
  selectedChain,
  onChainChange,
  chainsWithTokens,
  selectedWallet,
  onWalletChange,
  walletOptions,
  walletTotals,
  isWatchOnly,
  tokens,
  isRefreshing,
  onRefresh,
}: PortfolioHeaderProps) {
  const [isStuck, setIsStuck] = useState(false);
  const [isManualRefresh, setIsManualRefresh] = useState(false);
  const [stickyTopOffset, setStickyTopOffset] = useState(62);
  const { formatFiat, convertFromUsd, currencyCode } = useFiatDisplay();
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fixedHeader = document.getElementById(FIXED_HEADER_CONTAINER_ID);
    if (!fixedHeader || typeof ResizeObserver === 'undefined') return;

    const updateStickyTopOffset = () => {
      setStickyTopOffset(fixedHeader.getBoundingClientRect().height);
    };

    updateStickyTopOffset();
    const resizeObserver = new ResizeObserver(updateStickyTopOffset);
    resizeObserver.observe(fixedHeader);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Sentinel hidden -> sticky nav is stuck
        setIsStuck(!entry.isIntersecting);
      },
      {
        rootMargin: `-${stickyTopOffset}px 0px 0px 0px`,
        threshold: 0,
      },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [stickyTopOffset]);

  useEffect(() => {
    if (!isRefreshing && isManualRefresh) {
      setIsManualRefresh(false);
    }
  }, [isRefreshing, isManualRefresh]);

  const formatPnL = (value: number) => {
    const prefix = value > 0 ? '+' : '';
    return `${prefix}${formatFiat(value)}`;
  };

  const pnlColorClass = (value: number) => {
    if (value > 0) return 'text-token-green-1000';
    if (value < 0) return 'text-token-red-1000';
    return 'text-token-gray-700';
  };

  const change1dPercent = portfolio?.change1dPercent ?? 0;
  const change1dAbsolute = portfolio?.change1dAbsolute ?? 0;

  const netWorthSummary = isLoading ? (
    <div className="flex flex-col gap-2">
      <TextSkeleton width={180} height={32} />
      <TextSkeleton width={80} height={16} />
    </div>
  ) : (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <NumberFlow
          className={fontStyles({
            variant: 'H3',
            class: ['text-token-gray-1600', '[--number-flow-char-height:36px]', 'p-0'],
          })}
          value={convertFromUsd(totalValue)}
          format={{
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
        />
        {!isPortfolioLoading && portfolio && (
          <span
            className={fontStyles({
              variant: 'LABEL_LG',
              class: ['whitespace-nowrap', pnlColorClass(change1dPercent)],
            })}
          >
            {`${change1dPercent > 0 ? '↑ ' : change1dPercent < 0 ? '↓ ' : ''}${Math.abs(change1dPercent).toFixed(1)}% (${formatPnL(change1dAbsolute)})`}
          </span>
        )}
        {isPortfolioLoading && <TextSkeleton width={100} height={20} />}
      </div>
      <span className={fontStyles({ variant: 'LABEL_MD', class: 'text-token-gray-700 block' })}>
        Net worth
      </span>
    </div>
  );

  const refreshControl = onRefresh && !isLoading && (
    <Tooltip.Provider>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button
            onClick={() => {
              setIsManualRefresh(true);
              onRefresh();
            }}
            disabled={isRefreshing}
            className={btnStyles.SECONDARY_SM_ICON_ONLY}
            aria-label="Refresh portfolio"
            type="button"
          >
            <SpinningRefreshIcon isSpinning={isManualRefresh} />
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content side="top" sideOffset={8}>
            Refresh
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );

  return (
    <>
      {SHOW_WALLET_PNL && !isProd && (
        <div className="px-4">
          <div className="desktop:max-w-[1240px] mx-auto">
            <div className="flex flex-col tablet:flex-row tablet:justify-end gap-4 pt-5 mb-3">
              <div className="flex flex-col items-end gap-3">
                {!isLoading && !isPnLLoading && pnl && (
                  <div className="flex gap-8">
                    <div>
                      <span
                        className={fontStyles({
                          variant: 'LABEL_MD',
                          class: pnlColorClass(pnl.totalPnL),
                        })}
                      >
                        {formatPnL(pnl.totalPnL)} (
                        {pnl.totalPnL !== 0 && totalValue - pnl.totalPnL > 0
                          ? ((pnl.totalPnL / (totalValue - pnl.totalPnL)) * 100).toFixed(0)
                          : 0}
                        %)
                      </span>
                      <span
                        className={fontStyles({
                          variant: 'LABEL_MD',
                          class: 'text-token-gray-700 flex items-center gap-1 mt-2',
                        })}
                      >
                        Total PnL
                        <Tooltip.Provider>
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <button
                                type="button"
                                className="inline-flex text-token-gray-700 cursor-default"
                                aria-label="More information about total PnL"
                              >
                                <InfoIconOutline className="size-3.5" />
                              </button>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                              <Tooltip.Content
                                side="top"
                                sideOffset={8}
                                className="text-center max-w-[180px]"
                              >
                                Combined realized and unrealized profit or loss
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          </Tooltip.Root>
                        </Tooltip.Provider>
                      </span>
                    </div>
                    <div>
                      <span
                        className={fontStyles({
                          variant: 'LABEL_MD',
                          class: pnlColorClass(pnl.realizedGain),
                        })}
                      >
                        {formatPnL(pnl.realizedGain)} (
                        {pnl.realizedGain !== 0
                          ? ((pnl.realizedGain / Math.max(totalValue, 1)) * 100).toFixed(0)
                          : 0}
                        %)
                      </span>
                      <span
                        className={fontStyles({
                          variant: 'LABEL_MD',
                          class: 'text-token-gray-700 flex items-center gap-1 mt-2',
                        })}
                      >
                        Realized PnL
                        <Tooltip.Provider>
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <button
                                type="button"
                                className="inline-flex text-token-gray-700 cursor-default"
                                aria-label="More information about realized PnL"
                              >
                                <InfoIconOutline className="size-3.5" />
                              </button>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                              <Tooltip.Content
                                side="top"
                                sideOffset={8}
                                className="text-center max-w-[213px]"
                              >
                                Profit or loss from positions that have already been closed
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          </Tooltip.Root>
                        </Tooltip.Provider>
                      </span>
                    </div>
                    <div>
                      <span
                        className={fontStyles({
                          variant: 'LABEL_MD',
                          class: pnlColorClass(pnl.unrealizedGain),
                        })}
                      >
                        {formatPnL(pnl.unrealizedGain)} (
                        {pnl.unrealizedGain !== 0
                          ? ((pnl.unrealizedGain / Math.max(totalValue, 1)) * 100).toFixed(0)
                          : 0}
                        %)
                      </span>
                      <span
                        className={fontStyles({
                          variant: 'LABEL_MD',
                          class: 'text-token-gray-700 flex items-center gap-1 mt-2',
                        })}
                      >
                        Unrealized PnL
                        <Tooltip.Provider>
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <button
                                type="button"
                                className="inline-flex text-token-gray-700 cursor-default"
                                aria-label="More information about unrealized PnL"
                              >
                                <InfoIconOutline className="size-3.5" />
                              </button>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                              <Tooltip.Content
                                side="top"
                                sideOffset={8}
                                className="text-center max-w-[192px]"
                              >
                                Profit or loss from positions that are still open
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          </Tooltip.Root>
                        </Tooltip.Provider>
                      </span>
                    </div>
                  </div>
                )}
                {isPnLLoading && !isLoading && (
                  <div className="flex gap-8">
                    <div className="flex flex-col gap-2">
                      <TextSkeleton width={80} height={16} />
                      <TextSkeleton width={60} height={12} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <TextSkeleton width={80} height={16} />
                      <TextSkeleton width={60} height={12} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <TextSkeleton width={80} height={16} />
                      <TextSkeleton width={60} height={12} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div ref={sentinelRef} className="h-0 w-full" aria-hidden="true" />

      <div
        style={{ top: stickyTopOffset }}
        className={classNames(
          'sticky',
          'z-10 w-full',
          'transition-[background,backdrop-filter] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]',
          'py-2 mb-3 px-4',
          {
            'backdrop-blur-[15px] border-b border-token-alphalight-300': isStuck,
          },
        )}
      >
        <div className="flex h-[42px] w-full min-w-0 items-center">
          <div className="flex h-[42px] items-center desktop:max-w-[1240px] mx-auto w-full">
            {walletOptions.length > 0 && (
              <div className="px-2">
                <PortfolioWalletSelector
                  walletOptions={walletOptions}
                  selectedWallet={selectedWallet}
                  onWalletChange={onWalletChange}
                  walletTotals={walletTotals}
                  isWatchOnly={isWatchOnly}
                />
              </div>
            )}

            <div className="ml-2 min-w-0 flex-1">
              {isLoading ? (
                <div className="flex gap-2 overflow-hidden">
                  <TextSkeleton width={112} height={36} lineClassName="rounded-sm" />
                  <TextSkeleton width={96} height={36} lineClassName="rounded-sm" />
                  <TextSkeleton width={104} height={36} lineClassName="rounded-sm" />
                </div>
              ) : (
                chainsWithTokens.length > 1 && (
                  <PortfolioChainFilters
                    chains={chainsWithTokens}
                    selected={selectedChain}
                    onSelect={onChainChange}
                  />
                )
              )}
            </div>

            <div className="ml-2 flex shrink-0 items-center gap-2">{refreshControl}</div>
          </div>
        </div>
      </div>

      <div className="px-4">
        <div className="pb-5 desktop:max-w-[1240px] mx-auto">
          <div className="flex flex-col desktop:flex-row gap-6 w-full">
            <div className="w-full desktop:w-[70%] bg-special-background-card rounded-[26px] py-4">
              {isChartLoading ? (
                <PortfolioChartSkeleton
                  width={undefined}
                  height={280}
                  period={chartPeriod}
                  onPeriodChange={onChartPeriodChange}
                  summary={netWorthSummary}
                />
              ) : (
                <PortfolioChart
                  data={chart}
                  width={undefined}
                  height={280}
                  changePercent={change1dPercent}
                  period={chartPeriod}
                  onPeriodChange={onChartPeriodChange}
                  isLoading={isChartLoading}
                  summary={netWorthSummary}
                />
              )}
            </div>

            <div className="hidden w-full desktop:block desktop:w-[30%] bg-special-background-card rounded-[26px] p-4">
              {isLoading ? (
                <AllocationPieChartSkeleton size={192} />
              ) : (
                <AllocationPieChart
                  tokens={tokens}
                  totalValue={totalValue}
                  size={192}
                  maxTokens={8}
                  isLoading={isLoading}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
